import { type ReactNode, useMemo } from 'react';

import { DynamicWidgetIssueNotice } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_issue_notice';
import { getDynamicWidgetMetricTags } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_metrics';
import { type ClientDetailWidgetModel } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import {
    TEMPLATE_CARD_BODY_COLUMN_CLASS,
    TEMPLATE_CARD_CLASS,
    TEMPLATE_CARD_END_CLASS,
    TEMPLATE_CARD_FOOTER_CLASS,
    TEMPLATE_CARD_HEADER_LARGE_CLASS,
    TEMPLATE_CARD_SUBTITLE_CLASS,
    TEMPLATE_CARD_TITLE_LARGE_CLASS,
    TEMPLATE_OUTLINE_BUTTON_XS_CLASS,
    TEMPLATE_STATUS_CLASS,
    TEMPLATE_TAG_CLASS,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/template_theme';
import { StatusBadge } from '@apps/web/components/ui/status_badge';
import { UserAvatar } from '@apps/web/components/ui/user_avatar';
import { cn } from '@apps/web/lib/utils';

function DetailRow({
    label,
    children,
}: {
    readonly label: string;
    readonly children: ReactNode;
}) {
    return (
        <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-x-2.5 text-[13px]">
            <p className="dark:text-muted-foreground text-xs text-[#617578]">
                {label}
            </p>
            <div className="min-w-0">{children}</div>
        </div>
    );
}

export function ClientDetailWidget({
    model,
    loadError = false,
    onClientClick,
}: {
    readonly model: ClientDetailWidgetModel;
    readonly loadError?: boolean;
    readonly onClientClick?: (clientId: string) => void;
}) {
    const metricTags = useMemo(
        () => getDynamicWidgetMetricTags(model),
        [model],
    );
    const record = model.record;
    const clientId = record?.clientId ?? null;
    const displayName = record?.displayName ?? 'Client details unavailable';
    const location = [record?.city, record?.state]
        .filter(
            (value): value is string => value !== null && value !== undefined,
        )
        .join(', ');
    const isPreview = model.rowProvenance === 'event_preview';

    return (
        <div className={TEMPLATE_CARD_CLASS}>
            <div className={TEMPLATE_CARD_HEADER_LARGE_CLASS}>
                <UserAvatar
                    displayName={displayName}
                    colorKey={record?.clientId ?? displayName}
                    className="size-7"
                />
                <div className="min-w-0">
                    <p className={TEMPLATE_CARD_TITLE_LARGE_CLASS}>
                        {displayName}
                    </p>
                    <p className={cn(TEMPLATE_CARD_SUBTITLE_CLASS, 'truncate')}>
                        Client{location.length > 0 ? ` · ${location}` : ''}
                    </p>
                </div>
                <div className={TEMPLATE_CARD_END_CLASS}>
                    {record?.active !== null && record?.active !== undefined ? (
                        <StatusBadge
                            className={
                                TEMPLATE_STATUS_CLASS[
                                    record.active ? 'positive' : 'neutral'
                                ]
                            }
                            status={{
                                label: record.active ? 'Active' : 'Inactive',
                                intent: record.active ? 'positive' : 'neutral',
                            }}
                        />
                    ) : null}
                    {clientId !== null && onClientClick !== undefined ? (
                        <button
                            type="button"
                            className={TEMPLATE_OUTLINE_BUTTON_XS_CLASS}
                            onClick={() => onClientClick(clientId)}
                        >
                            Open client
                        </button>
                    ) : null}
                </div>
            </div>
            {model.issues.map((issue) => (
                <div key={issue.id} className="px-3.5 pt-2.5">
                    <DynamicWidgetIssueNotice
                        issue={issue}
                        metricTags={metricTags}
                    />
                </div>
            ))}
            {record !== null ? (
                <div className={TEMPLATE_CARD_BODY_COLUMN_CLASS}>
                    {record.preferredName !== null ? (
                        <DetailRow label="Preferred name">
                            {record.preferredName}
                        </DetailRow>
                    ) : null}
                    {record.office !== null ? (
                        <DetailRow label="Office">{record.office}</DetailRow>
                    ) : null}
                    {location.length > 0 ? (
                        <DetailRow label="Location">{location}</DetailRow>
                    ) : null}
                    {record.tags.length > 0 ? (
                        <DetailRow label="Tags">
                            <div className="flex flex-wrap gap-1">
                                {record.tags.map((tag) => (
                                    <span
                                        key={tag}
                                        className={TEMPLATE_TAG_CLASS}
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </DetailRow>
                    ) : null}
                </div>
            ) : (
                <p className="text-muted-foreground px-3.5 py-3 text-xs">
                    Loading the client record…
                </p>
            )}
            {isPreview ? (
                <div className={TEMPLATE_CARD_FOOTER_CLASS}>
                    <p className="text-muted-foreground ml-auto text-xs">
                        {loadError
                            ? 'Preview only — the full client artifact is unavailable.'
                            : 'Preview · full record is loading'}
                    </p>
                </div>
            ) : null}
        </div>
    );
}
